import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import {
  checkCoefficientInputAnswer,
  CoefficientInputActivityDataModel,
  generateCoefficientInputActivityData,
} from './CoefficientInput.utils'

interface UseCoefficientInputReturnType {
  operators: string[]
  variable: string
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseCoefficientInputProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseCoefficientInput({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseCoefficientInputProps): UseCoefficientInputReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [activityData, setActivityData] = useState<CoefficientInputActivityDataModel>({
    answer: 0,
    operators: [],
    variable: '',
    maxHistoryLength: 3,
  })
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateCoefficientInputActivityData(difficultyLevel))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(checkCoefficientInputAnswer(activityData.answer, answer))
  }, [onAnswerStateChange, answer, activityData.answer])

  const onInputChange = useCallback((value: string) => {
    setAnswer(value)
  }, [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) =>
      [activityData.operators.join(''), ...prevState].slice(0, activityData.maxHistoryLength),
    )
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.operators, activityData.maxHistoryLength])

  return {
    operators: activityData.operators,
    variable: activityData.variable,
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseCoefficientInput
